import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { ChangeDetectionStrategy, Component, effect, inject, input, output, signal, ViewEncapsulation } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { Store } from '@ngrx/store';
import { PRODUCTION_COMMAND_BUTTONS } from '@src/suite/common/enums/constants';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { ML2HtmlUtils, ProductionCommandsTitleKey, ProductionCommandType } from '@src/suite/common/utils/ml-2-html-utils';
import FroalaEditor from 'froala-editor';

@Component({
    selector: 'st-story-toolbar',
    imports: [MatIcon, MatIconButton, MatTooltip],
    templateUrl: './st-story-toolbar.html',
    styleUrl: './st-story-toolbar.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-story-toolbar',
    },
})
export class StStoryToolbarComponent {
    store = inject(Store);
    // Inputs
    readonly editor = input<FroalaEditor | null>(null);
    readonly disabled = input(false, { transform: coerceBooleanProperty });
    readonly hasUnsavedChanges = input(false, { transform: coerceBooleanProperty });

    // Outputs
    readonly saveClicked = output<void>();
    readonly productionCommandClicked = output<ProductionCommandType>();

    // Active-state signals refreshed on every Froala event
    readonly isBold = signal(false);
    readonly isItalic = signal(false);
    readonly isUnderline = signal(false);
    readonly isStrikeThrough = signal(false);
    readonly canUndo = signal(false);
    readonly canRedo = signal(false);
    readonly paragraphFormat = signal<string>('N');

    readonly i18n = this.store.selectSignal(selectI18N);

    readonly productionCommandButtons = PRODUCTION_COMMAND_BUTTONS;

    constructor() {
        /**
         * Keeps the toolbar button active states (bold, italic, underline, strikethrough)
         * in sync with the current selection inside the Froala editor.
         *
         * ## Why event listeners are needed
         *
         * The active-state signals (isBold, isItalic, …) drive Angular bindings such as
         * `[class.st-story-toolbar__btn--active]="isBold()"`. Angular only re-evaluates
         * those bindings when a signal changes, so we must push a new value into each
         * signal whenever the selection or formatting in the editor changes. The listeners
         * below call `refreshState()` at the right moments to do exactly that.
         *
         * ## Why these four events
         *
         * - `keyup`         — the user moved the caret with arrow keys or typed, potentially
         *                     landing in text with different formatting.
         * - `mouseup`       — the user clicked or finished a drag-select; the selection is
         *                     fully committed by mouseup, so the format read is accurate.
         * - `focus`         — the editor just received focus; refresh immediately so the
         *                     toolbar reflects the format at the caret from the first moment.
         * - `commands.after`— Froala fired a formatting command (e.g. Bold toggle); refresh
         *                     so the button active state reflects the result without waiting
         *                     for the next keyup or mouseup.
         *
         * ## Cleanup strategy
         *
         * `keyup`, `mouseup` and `focus` are standard DOM events on the editable element
         * (`ed.el`). We use `addEventListener` / `removeEventListener` so they are cleaned
         * up symmetrically in `onCleanup`.
         *
         * `commands.after` is a Froala-internal synthetic event that exists only on
         * Froala's own event bus (`ed.events`). Froala provides no `events.off()` method,
         * so the listener cannot be removed. Instead we use a `disposed` flag: `onCleanup`
         * sets it to `true`, turning the listener into a no-op for the lifetime of the
         * old editor instance after the effect has re-run with a new one.
         *
         * The whole `effect` re-runs whenever `editor()` changes (e.g. story switch),
         * registering fresh listeners on the incoming editor and disposing the old ones.
         */
        effect(onCleanup => {
            const ed = this.editor();
            if (!ed) return;

            const refresh = () => this.refreshState(ed);

            ed.el.addEventListener('keyup', refresh);
            ed.el.addEventListener('mouseup', refresh);
            ed.el.addEventListener('focus', refresh);

            // TODO: not fully sure I want it that way, but since Froala provides no way to remove this listener, we must ensure it does not call refresh() after the editor is disposed
            let disposed = false;
            ed.events.on(
                'commands.after',
                () => {
                    if (!disposed) refresh();
                },
                false
            );

            onCleanup(() => {
                ed.el.removeEventListener('keyup', refresh);
                ed.el.removeEventListener('mouseup', refresh);
                ed.el.removeEventListener('focus', refresh);
                disposed = true;
            });

            // Populate initial state as soon as the editor is ready.
            this.refreshState(ed);
        });
    }

    private refreshState(ed: FroalaEditor): void {
        // document.queryCommandState() is deprecated by the spec but remains the only reliable
        // way to read the browser's computed inline-format state at the current selection.
        // Froala itself uses it internally for the same purpose, so it is safe to rely on here.
        // Froala's ed.format.is() checks its internal element model and does not reflect the
        // actual caret state correctly, so we fall back to the browser API here.
        // TODO: if Froala ever provides a reliable API for this, we should switch to it and stop using the deprecated browser API.
        this.isBold.set(document.queryCommandState('bold'));
        this.isItalic.set(document.queryCommandState('italic'));
        this.isUnderline.set(document.queryCommandState('underline'));
        this.isStrikeThrough.set(document.queryCommandState('strikeThrough'));

        // Both undo and redo checks live on ed.undo — there is no ed.redo object in Froala.
        // canDo() → undo steps are available; canRedo() → redo steps are available.
        this.canUndo.set(ed.undo?.canDo?.() ?? false);
        this.canRedo.set(ed.undo?.canRedo?.() ?? false);

        // Detect paragraph format via Froala's paragraphFormat plugin.
        // ed.paragraphFormat.is() checks the block tag at the current caret position.
        const formats = ['H1', 'H2', 'H3', 'H4'];
        const active = formats.find(f => ed.paragraphFormat?.is?.(f));
        this.paragraphFormat.set(active ?? 'N');
    }

    getCommandIconParams(commandType: ProductionCommandType): { icon: string; fontSet: string } {
        return ML2HtmlUtils.getCommandTypeIconParams(commandType);
    }

    getCommandTooltip(commandType: ProductionCommandType): string {
        const key = ProductionCommandsTitleKey[commandType];
        return key ? (this.i18n()[key] ?? commandType) : commandType;
    }

    onSave(): void {
        this.saveClicked.emit();
        const ed = this.editor();
        if (!ed) return;
        ed.el.focus();
    }

    onFormat(command: 'bold' | 'italic' | 'underline' | 'strikeThrough'): void {
        const ed = this.editor();
        if (!ed) return;
        ed.commands[command]();
    }

    onUndo(): void {
        this.editor()?.commands.undo();
    }

    onRedo(): void {
        this.editor()?.commands.redo();
    }

    onCopyText(): void {
        const ed = this.editor();
        if (!ed) return;
        // mousedown.preventDefault() keeps focus on the editor, so the selection is intact
        // and the browser allows execCommand('copy') because we are inside a user gesture.
        ed.el.focus();
        document.execCommand('copy');
    }

    onCutText(): void {
        const ed = this.editor();
        if (!ed) return;
        ed.el.focus();
        document.execCommand('cut');
    }

    onPasteText(): void {
        const ed = this.editor();
        if (!ed) return;
        // document.execCommand('paste') is blocked by all modern browsers for security.
        // Save the Froala selection before the async Clipboard API call so we can restore
        // the caret position after the Promise resolves (focus may have shifted by then).
        ed.el.focus();
        ed.selection.save();
        navigator.clipboard
            .readText()
            .then(text => {
                if (!text) return;
                ed.el.focus();
                ed.selection.restore();
                ed.html.insert(text);
            })
            .catch(() => {
                // Clipboard read permission denied or Clipboard API unavailable.
            });
    }

    onProductionCommand(commandType: ProductionCommandType): void {
        this.productionCommandClicked.emit(commandType);
    }
}
